import { useState, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DatePickerProps {
  value: string
  min?: string
  onChange: (val: string) => void
}

export function DatePicker({ value, min, onChange }: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [month, setMonth] = useState(new Date())
  const pickerRef = useRef<HTMLDivElement>(null)

  const currentDate = value ? new Date(value) : null

  useEffect(() => {
    if (value) {
      const date = new Date(value)
      setMonth(new Date(date.getFullYear(), date.getMonth(), 1))
    }
  }, [value])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const minDate = min ? new Date(min) : null
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const daysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  const firstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay()

  const days = []
  const totalDays = daysInMonth(month)
  const startDay = firstDayOfMonth(month)

  for (let i = 0; i < startDay; i++) days.push(null)
  for (let i = 1; i <= totalDays; i++) days.push(i)

  function formatDate(date: Date): string {
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    const y = date.getFullYear()
    return `${y}-${m}-${d}`
  }

  function handleDateClick(day: number) {
    const selected = new Date(month.getFullYear(), month.getMonth(), day)
    onChange(formatDate(selected))
    setIsOpen(false)
  }

  function handlePrevMonth() {
    setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
  }

  function handleNextMonth() {
    setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
  }

  const isDateDisabled = (day: number) => {
    const date = new Date(month.getFullYear(), month.getMonth(), day)
    if (minDate) {
      const minDateOnly = new Date(minDate)
      minDateOnly.setHours(0, 0, 0, 0)
      return date < minDateOnly
    }
    return date < today
  }

  const displayValue = currentDate
    ? `${String(currentDate.getMonth() + 1).padStart(2, '0')}/${String(currentDate.getDate()).padStart(2, '0')}/${currentDate.getFullYear()}`
    : ''

  const monthName = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  return (
    <div ref={pickerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-12 border border-gray-300 rounded-xl px-3 py-3 text-sm focus:outline-none transition-colors focus:border-blue-500 text-left flex items-center justify-between"
      >
        <span className={currentDate ? 'text-gray-900 font-medium' : 'text-gray-400'}>
          {displayValue}
        </span>
        <Calendar size={18} className="text-gray-400 flex-shrink-0 ml-2" />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4 w-80">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={18} className="text-gray-600" />
            </button>
            <span className="font-semibold text-gray-900">{monthName}</span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight size={18} className="text-gray-600" />
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="text-center text-xs font-semibold text-gray-400 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, idx) => {
              const isDisabled = day ? isDateDisabled(day) : false
              const isSelected =
                day && currentDate &&
                day === currentDate.getDate() &&
                month.getMonth() === currentDate.getMonth() &&
                month.getFullYear() === currentDate.getFullYear()

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => day && !isDisabled && handleDateClick(day)}
                  disabled={!day || isDisabled}
                  className={cn(
                    'py-2 text-sm rounded-lg transition-colors font-medium',
                    !day || isDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : isSelected
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  {day}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
